<script lang="ts">
  import { onMount } from 'svelte';
  import { t } from '../i18n/index.svelte';
  import { drawToCanvas } from '../image';
  import { TEXTURE_PRESETS, type TexturePreset } from '../textures/presets';
  import type { TextureSpec } from '../types';
  import { batchPool } from '../wasm/pool';

  let { current, onpick }: { current: TextureSpec; onpick: (spec: TextureSpec) => void } = $props();

  const canvases: Record<string, HTMLCanvasElement> = {};
  const SIZE = 64;

  onMount(() => {
    // Thumbnails are rendered by the same WASM code as the stereograms.
    for (const p of TEXTURE_PRESETS) {
      batchPool()
        .call('renderTexture', { texture: p.spec, width: SIZE, height: SIZE, seed: 7 })
        .then((img) => canvases[p.id] && drawToCanvas(canvases[p.id], img))
        .catch(() => {});
    }
  });

  const isCurrent = (p: TexturePreset) => JSON.stringify(p.spec) === JSON.stringify(current);
</script>

<div class="presets" data-testid="texture-presets">
  {#each TEXTURE_PRESETS as p (p.id)}
    <button
      class="preset"
      class:on={isCurrent(p)}
      title={t(`texture.preset.${p.id}` as never)}
      onclick={() => onpick(structuredClone(p.spec))}
      data-testid={`preset-${p.id}`}
    >
      <canvas bind:this={canvases[p.id]} width={SIZE} height={SIZE}></canvas>
      <span>{t(`texture.preset.${p.id}` as never)}</span>
    </button>
  {/each}
</div>

<style>
  .presets {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 6px;
  }
  .preset {
    padding: 3px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    border-radius: 6px;
    min-width: 0;
  }
  .preset.on {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
  }
  canvas {
    width: 100%;
    aspect-ratio: 1;
    border-radius: 4px;
    image-rendering: pixelated;
  }
  span {
    font-size: 10px;
    line-height: 1.1;
    color: var(--muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
</style>
